// services/LocalStorageService.ts
interface LocalRecording {
  id: string;
  audioBlob: Blob;
  metadata: {
    patientId: string;
    patientClinicOSUUID?: string;
    patientName?: string;
    noteType: string;
    timestamp: string;
    clinicId: string;
    userEmail?: string;
    clinicName?: string;
    noteDateOverride?: string;
  };
  status: "stored" | "uploading" | "uploaded" | "failed";
  uploadAttempts: number;
  lastAttempt?: string;
}

class LocalStorageService {
  private dbName = "VoiceRecordingsDB";
  private dbVersion = 1;
  private storeName = "recordings";
  private metadataKey = "voice_recordings_metadata";

  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: "id" });
          store.createIndex("status", "status", { unique: false });
          store.createIndex("timestamp", "metadata.timestamp", {
            unique: false,
          });
        }
      };
    });
  }

  async storeRecording(
    recording: Omit<LocalRecording, "id" | "status" | "uploadAttempts">,
  ): Promise<string> {
    const id = `recording_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const fullRecording: LocalRecording = {
      ...recording,
      id,
      status: "stored",
      uploadAttempts: 0,
    };

    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      await new Promise<void>((resolve, reject) => {
        const request = store.add(fullRecording);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Update metadata in localStorage
      this.updateMetadata(id, fullRecording.metadata, "stored");

      console.log(`📱 Stored recording locally: ${id}`);
      return id;
    } catch (error) {
      console.error("Error storing recording locally:", error);
      throw error;
    }
  }

  async getRecording(id: string): Promise<LocalRecording | null> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Error getting recording:", error);
      return null;
    }
  }

  async updateRecordingStatus(
    id: string,
    status: LocalRecording["status"],
    incrementAttempts = false,
  ): Promise<void> {
    try {
      const recording = await this.getRecording(id);
      if (!recording) return;

      recording.status = status;
      recording.lastAttempt = new Date().toISOString();
      if (incrementAttempts) {
        recording.uploadAttempts += 1;
      }

      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      await new Promise<void>((resolve, reject) => {
        const request = store.put(recording);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Update metadata
      this.updateMetadata(id, recording.metadata, status);
    } catch (error) {
      console.error("Error updating recording status:", error);
    }
  }

  async deleteRecording(id: string): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Remove from metadata
      this.removeFromMetadata(id);

      console.log(`🗑️ Deleted local recording: ${id}`);
    } catch (error) {
      console.error("Error deleting recording:", error);
    }
  }

  async getFailedRecordings(): Promise<LocalRecording[]> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const index = store.index("status");

      return new Promise((resolve, reject) => {
        const request = index.getAll("failed");
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Error getting failed recordings:", error);
      return [];
    }
  }

  async getPendingRecordings(): Promise<LocalRecording[]> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          const allRecordings = request.result;
          const pending = allRecordings.filter(
            (r) => r.status === "stored" || r.status === "failed",
          );
          resolve(pending);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Error getting pending recordings:", error);
      return [];
    }
  }

  private updateMetadata(
    id: string,
    metadata: LocalRecording["metadata"],
    status: string,
  ): void {
    try {
      const existing = JSON.parse(
        localStorage.getItem(this.metadataKey) || "{}",
      );
      existing[id] = {
        ...metadata,
        status,
        lastUpdated: new Date().toISOString(),
      };
      localStorage.setItem(this.metadataKey, JSON.stringify(existing));
    } catch (error) {
      console.error("Error updating metadata:", error);
    }
  }

  private removeFromMetadata(id: string): void {
    try {
      const existing = JSON.parse(
        localStorage.getItem(this.metadataKey) || "{}",
      );
      delete existing[id];
      localStorage.setItem(this.metadataKey, JSON.stringify(existing));
    } catch (error) {
      console.error("Error removing from metadata:", error);
    }
  }

  async cleanup(): Promise<void> {
    try {
      // Clean up recordings older than 24 hours that are uploaded or failed with >3 attempts
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      const request = store.getAll();
      request.onsuccess = () => {
        const recordings = request.result;
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

        recordings.forEach((recording) => {
          const recordingTime = new Date(
            recording.metadata.timestamp,
          ).getTime();
          const shouldDelete =
            recordingTime < oneDayAgo &&
            (recording.status === "uploaded" ||
              (recording.status === "failed" && recording.uploadAttempts >= 3));

          if (shouldDelete) {
            this.deleteRecording(recording.id);
          }
        });
      };
    } catch (error) {
      console.error("Error during cleanup:", error);
    }
  }

  async getStorageStats(): Promise<{
    totalRecordings: number;
    pendingRecordings: number;
    failedRecordings: number;
  }> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          const recordings = request.result;
          const stats = {
            totalRecordings: recordings.length,
            pendingRecordings: recordings.filter((r) => r.status === "stored")
              .length,
            failedRecordings: recordings.filter((r) => r.status === "failed")
              .length,
          };
          resolve(stats);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Error getting storage stats:", error);
      return { totalRecordings: 0, pendingRecordings: 0, failedRecordings: 0 };
    }
  }
}

export default new LocalStorageService();
